import { devError } from '../../utils/devLog';
import React, {useState, useMemo} from 'react';
import {Box, Menu, MenuButton, MenuList, MenuItem, Button, Text, useToast, VStack, HStack, MenuDivider, Badge, Switch} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import {ImportModal} from '../features';
import {ParsedChartData} from '../../models/types';
import {ChartMetadata} from '../../models/storage';
import {getStorageService} from '../../services/StorageService';
import RecentFilesList from '../features/storage/RecentFilesList';
import LoadModal from '../features/storage/LoadModal';
import NewChartModal from '../features/chart/NewChartModal';
import Sidebar from './Sidebar';
import { ProfilePage } from '../../pages/ProfilePage';
import { useMultipleCharts } from '../common/AstrologyChart/MultipleChartsContext';
import { createChartManager, ToastConfig } from '../../services/ChartManager';
import { SharedViewMode } from '../../types/share.types';
import './Header.css';

interface HeaderProps {
    onImportData?: (data: ParsedChartData) => void;
    onCreateChart?: (data: ParsedChartData) => void;
    onSaveChart?: (data: ParsedChartData, chartName: string) => Promise<void>;
    shareMode?: boolean;  // If true, hide Data menu and Sidebar
    isAuthor?: boolean;  // If true in share mode, user is the author
    sharedViewMode?: SharedViewMode;  // View mode for shared charts
    onSharedViewModeChange?: (mode: SharedViewMode) => void;  // Callback for view mode change
}

/**
 * Header component with CSS styles for easy editing
 */
const Header: React.FC<HeaderProps> = ({
    onImportData,
    onCreateChart,
    onSaveChart,
    shareMode = false,
    isAuthor = false,
    sharedViewMode = 'author',
    onSharedViewModeChange
}) => {
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
    const [isNewChartModalOpen, setIsNewChartModalOpen] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activePanel, setActivePanel] = useState<string | null>(null);
    const [currentChart, setCurrentChart] = useState<ParsedChartData | null>(null);
    const [currentChartName, setCurrentChartName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isChartSaved, setIsChartSaved] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Use MultipleChartsContext for workspace operations
    const {
        loadChart: loadChartToWorkspace,
        getActiveChart,
        loadedChartIds,
        workspace,
        charts
    } = useMultipleCharts();

    // Create ChartManager instance
    const chartManager = useMemo(() => {
        const showToastHandler = (config: ToastConfig) => {
            toast({
                ...config,
                isClosable: true,
                position: 'top-right'
            });
        };

        // Callback to get content hashes of loaded charts for duplicate detection
        const getLoadedContentHashes = (): Map<string, string> => {
            const hashes = new Map<string, string>();
            for (const [chartId, chart] of charts) {
                if (chart.metadata.contentHash) {
                    hashes.set(chartId, chart.metadata.contentHash);
                }
            }
            return hashes;
        };

        return createChartManager(
            loadChartToWorkspace,
            loadedChartIds,
            workspace,
            showToastHandler,
            getLoadedContentHashes
        );
    }, [loadChartToWorkspace, loadedChartIds, workspace, toast, charts]);

    const handleImportClick = () => {
        setIsImportModalOpen(true);
    };

    const handleLoadClick = () => {
        setIsLoadModalOpen(true);
    };

    const handleNewChartClick = () => {
        setIsNewChartModalOpen(true);
    };

    // Expose new chart modal handler globally
    React.useEffect(() => {
        (window as any).__openNewChartModal = handleNewChartClick;
    }, []);
    
    const handleSaveClick = async () => {
        if (currentChart && onSaveChart && currentChartName.trim()) {
            try {
                setIsSaving(true);
                await onSaveChart(currentChart, currentChartName.trim());
                setIsChartSaved(true); // Mark chart as saved
                // Don't show toast here - MainLayout will show it
            } catch (error) {
                devError('Save failed:', error);
                toast({
                    title: 'Save Failed',
                    description: 'Failed to save chart',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                    position: 'top-right'
                });
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleImportData = async (data: ParsedChartData, savedChartId?: string) => {
        try {
            let result;

            if (savedChartId) {
                // Chart already saved, just load it
                result = await chartManager.loadChartFromStorage(
                    savedChartId,
                    { name: 'Imported Chart' } as ChartMetadata,
                    { makeActive: true }
                );
            } else {
                // Import and save chart
                result = await chartManager.importChartData(data, 'Imported Chart');
            }

            if (result.success) {
                // Legacy support for onImportData prop (if provided)
                if (onImportData) {
                    await onImportData(data);
                }

                setCurrentChart(data);
                setCurrentChartName('Imported Chart');
                setIsChartSaved(true);
            }

        } catch (error) {
            devError('❌ [Header] Failed to import chart:', error);
        }
    };

    const handleImportModalClose = () => {
        setIsImportModalOpen(false);
    };

    const handleLoadModalClose = () => {
        setIsLoadModalOpen(false);
    };

    const handleNewChartModalClose = () => {
        setIsNewChartModalOpen(false);
    };

    const handleLoadChart = async (id: string, metadata: ChartMetadata) => {
        const result = await chartManager.loadChartFromStorage(id, metadata, { makeActive: true });

        if (result.success) {
            // Update local state for legacy compatibility
            try {
                const storageService = getStorageService();
                const chartResult = await storageService.loadChart(id);

                if (chartResult.success && chartResult.data) {
                    setCurrentChart(chartResult.data.chartData);
                    setCurrentChartName(chartResult.data.metadata.name);
                    setIsChartSaved(true);
                    // Debug: Loading chart from Load Modal (production: logs disabled)
                }
            } catch (error) {
                devError('Error updating local state:', error);
            }
        }
    };

    const handleRecentFileSelect = async (id: string, metadata: ChartMetadata) => {
        const result = await chartManager.loadChartFromStorage(
            id,
            metadata,
            {
                makeActive: true,
                insertAtIndex: loadedChartIds.length
            }
        );

        if (result.success) {
            // Update local state for legacy compatibility
            try {
                const storageService = getStorageService();
                const chartResult = await storageService.loadChart(id);

                if (chartResult.success && chartResult.data) {
                    setCurrentChart(chartResult.data.chartData);
                    setCurrentChartName(chartResult.data.metadata.name);
                    setIsChartSaved(true);
                }
            } catch (error) {
                devError('Error updating local state:', error);
            }
        }
    };

    const handleSelectPanel = (panelType: string) => {
        setActivePanel(panelType);
    };

    const handleClosePanel = () => {
        setActivePanel(null);
    };

    const handleCloseSidebar = () => {
        setIsSidebarOpen(false);
        setActivePanel(null); // Close content panel when sidebar closes
    };

    // Render panel content based on active panel
    const renderPanelContent = () => {
        switch (activePanel) {
            case 'profile':
                return <ProfilePage />;
            default:
                return null;
        }
    };

    const getPanelTitle = () => {
        switch (activePanel) {
            case 'profile':
                return 'User Profile';
            default:
                return '';
        }
    };

    // Handle title click
    const handleTitleClick = () => {
        if (shareMode) {
            // In share mode, navigate to home page
            navigate('/');
        } else {
            // In normal mode, open sidebar
            setIsSidebarOpen(true);
        }
    };

    return (
        <Box as="header" className="hora-header">
            {/* Hora Title - Clickable to open Sidebar (normal) or go home (share mode) */}
            <Text
                className="hora-title"
                onClick={handleTitleClick}
                cursor="pointer"
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
            >
                Bhrigu Hora
            </Text>

            {/* View Mode Toggle - Only visible when shareMode && isAuthor */}
            {shareMode && isAuthor && onSharedViewModeChange && (
                <HStack
                    spacing={3}
                    position="absolute"
                    right="20px"
                    top="50%"
                    transform="translateY(-50%)"
                >
                    <Badge
                        colorScheme={sharedViewMode === 'author' ? 'green' : 'gray'}
                        fontSize="sm"
                        px={2}
                        py={1}
                        cursor="pointer"
                        onClick={() => onSharedViewModeChange('author')}
                        _hover={{ opacity: 0.8 }}
                    >
                        Author
                    </Badge>
                    <Switch
                        size="md"
                        isChecked={sharedViewMode === 'guest'}
                        onChange={(e) => {
                            onSharedViewModeChange(e.target.checked ? 'guest' : 'author');
                        }}
                        colorScheme="blue"
                    />
                    <Badge
                        colorScheme={sharedViewMode === 'guest' ? 'blue' : 'gray'}
                        fontSize="sm"
                        px={2}
                        py={1}
                        cursor="pointer"
                        onClick={() => onSharedViewModeChange('guest')}
                        _hover={{ opacity: 0.8 }}
                    >
                        Guest
                    </Badge>
                </HStack>
            )}

            {/* File Menu - hidden in share mode */}
            {!shareMode && (
            <Menu>
                <MenuButton
                    as={Button}
                    variant="ghost"
                    className="hora-menu-button"
                >
                    Chart
                </MenuButton>
                <MenuList className="hora-menu-list">
                    <MenuItem 
                        onClick={handleNewChartClick}
                        className="hora-menu-item"
                    >
                        ⭐ New Chart
                    </MenuItem>
                    <MenuItem 
                        onClick={handleImportClick}
                        className="hora-menu-item"
                    >
                        📁 Import
                    </MenuItem>
                    <MenuItem
                        onClick={handleLoadClick}
                        className="hora-menu-item"
                    >
                        📂 Load
                    </MenuItem>

                    {/* Current Chart Save Section - only show if chart is not saved */}
                    {currentChart && !isChartSaved && (
                        <>
                            <MenuDivider />
                            <Box className="save-section-header">
                                <Text className="save-section-title">
                                    SAVE Current Chart
                                </Text>
                            </Box>
                            <MenuItem
                                className="save-chart-item"
                                onClick={handleSaveClick}
                                isDisabled={isSaving || !currentChartName.trim()}
                            >
                                <VStack align="start" spacing={0} width="100%">
                                    <HStack justify="space-between" width="100%">
                                        <Text className="save-chart-name">
                                            {isSaving ? 'Saving...' : currentChartName}
                                        </Text>
                                        {isSaving && (
                                            <Text className="save-chart-saving">
                                                💾
                                            </Text>
                                        )}
                                    </HStack>
                                    {currentChart.chartDateTime && (
                                        <Text className="save-chart-details">
                                            {currentChart.chartDateTime.date} {currentChart.chartDateTime.time}
                                            {currentChart.chartDateTime.location && ` • ${currentChart.chartDateTime.location}`}
                                        </Text>
                                    )}
                                </VStack>
                            </MenuItem>
                        </>
                    )}
                    
                    {/* Recent Files List */}
                    <RecentFilesList onFileSelect={handleRecentFileSelect}/>
                </MenuList>
            </Menu>
            )}

            {/* Import Modal */}
            <ImportModal
                isOpen={isImportModalOpen}
                onClose={handleImportModalClose}
                onImport={handleImportData}
            />

            {/* Load Modal */}
            <LoadModal
                isOpen={isLoadModalOpen}
                onClose={handleLoadModalClose}
                onLoadChart={handleLoadChart}
            />

            {/* New Chart Modal */}
            <NewChartModal
                isOpen={isNewChartModalOpen}
                onClose={handleNewChartModalClose}
                onCreateChart={async (data, chartName) => {
                    // Debug: onCreateChart called (production: logs disabled)

                    const result = await chartManager.createNewChart(data, chartName || 'New Chart');
                    // Debug: ChartManager createNewChart result (production: logs disabled)

                    if (result.success) {
                        // Store current chart data for compatibility
                        setCurrentChart(data);
                        setCurrentChartName(chartName || 'New Chart');
                        setIsChartSaved(true);

                        // Debug: Chart created successfully (production: logs disabled)

                        // Call legacy handler if provided
                        if (onCreateChart) {
                            try {
                                // Debug: Calling legacy onCreateChart handler (production: logs disabled)
                                await onCreateChart(data);
                                // Debug: Legacy onCreateChart handler completed (production: logs disabled)
                            } catch (error) {
                                devError('❌ [Header] Legacy onCreateChart handler error:', error);
                            }
                        } else {
                            // Debug: No legacy onCreateChart handler provided (production: logs disabled)
                        }
                    } else {
                        devError('❌ [Header] Chart creation failed:', result);
                    }
                }}
                onSaveChart={onSaveChart}
            />

            {/* Sidebar with integrated Content Panel - hidden in share mode */}
            {!shareMode && (
            <Sidebar
                isOpen={isSidebarOpen}
                onClose={handleCloseSidebar}
                onSelectPanel={handleSelectPanel}
                activePanel={activePanel}
                onClosePanel={handleClosePanel}
                panelContent={renderPanelContent()}
                panelTitle={getPanelTitle()}
            />
            )}

        </Box>
    );
};

export default Header;